/**
 * Triggers a browser file download of the extension package (e.g. .zip for “Load unpacked”).
 * Set REACT_APP_EXTENSION_PACKAGE_URL to a direct .zip (or use REACT_APP_EXTENSION_INSTALL_URL
 * when that URL already points to a .zip / .crx / .xpi). Otherwise the install help modal opens.
 */
import { detectExtensionBrowserTargetFromNavigator, isChromeWebStoreInstallUrl, } from '@flow-doc/shared';
import { DEFAULT_EXTENSION_PACKAGE_FILENAME, EXTENSION_BANNER_OPEN_INSTALL_MODAL_EVENT, } from '../constants/extensionInstall';
import { apiUrl } from './api';
const FILE_EXT = /\.(zip|crx|xpi)(?:$|[?#])/i;
const EXTENSION_ARTIFACT_METADATA_PATH = '/api/extension/artifacts/latest';
const EXTENSION_ARTIFACT_DOWNLOAD_PATH = '/api/extension/artifacts/latest/download';
function getDetectedBrowserTarget() {
    return detectExtensionBrowserTargetFromNavigator(typeof navigator !== 'undefined' ? navigator : { userAgent: '' });
}
function getExtensionArtifactMetadataApiPath(browser = getDetectedBrowserTarget()) {
    return `${EXTENSION_ARTIFACT_METADATA_PATH}?browser=${encodeURIComponent(browser)}`;
}
function getExtensionArtifactDownloadApiPath(browser = getDetectedBrowserTarget()) {
    return `${EXTENSION_ARTIFACT_DOWNLOAD_PATH}?browser=${encodeURIComponent(browser)}`;
}
/** Strips one pair of surrounding quotes if present (common .env / copy-paste). */
function normalizeEnvString(value) {
    if (!value)
        return '';
    const t = value.trim();
    if (t.length >= 2) {
        const a = t[0];
        const b = t[t.length - 1];
        if (a === b && (a === '"' || a === "'")) {
            return t.slice(1, -1).trim();
        }
    }
    return t;
}
function isBundledFileUrl(href) {
    if (!href || href === '/extension')
        return false;
    return FILE_EXT.test(href);
}
function readStringProperty(record, key) {
    const value = record[key];
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}
function isExtensionArtifactDownloadMetadata(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const record = value;
    const browserTarget = readStringProperty(record, 'browserTarget');
    return (readStringProperty(record, 'id') !== undefined &&
        readStringProperty(record, 'version') !== undefined &&
        readStringProperty(record, 'filename') !== undefined &&
        /^[a-f0-9]{64}$/i.test(readStringProperty(record, 'sha256') ?? '') &&
        (browserTarget === 'chrome' || browserTarget === 'edge'));
}
/**
 * True when `REACT_APP_EXTENSION_INSTALL_URL` points at the Chrome Web Store (one-click install).
 * @deprecated Prefer {@link isChromiumExtensionStoreInstallUrl} from `@flow-doc/shared` when Edge Add-ons should count as a store install.
 */
export function isWebStoreUrl(value) {
    return isChromeWebStoreInstallUrl(value);
}
export { isChromiumExtensionStoreInstallUrl } from '@flow-doc/shared';
export function getExtensionPackageDownloadUrl() {
    return getConfiguredExtensionPackageDownloadUrl() || apiUrl(getExtensionArtifactDownloadApiPath());
}
function getConfiguredExtensionPackageDownloadUrl() {
    const direct = normalizeEnvString(process.env.REACT_APP_EXTENSION_PACKAGE_URL);
    if (direct && isBundledFileUrl(direct)) {
        return direct;
    }
    const install = normalizeEnvString(process.env.REACT_APP_EXTENSION_INSTALL_URL);
    if (isBundledFileUrl(install)) {
        return install ?? '';
    }
    return '';
}
export function getExtensionPackageFileName(fallbackUrl) {
    const fromEnv = normalizeEnvString(process.env.REACT_APP_EXTENSION_PACKAGE_FILENAME);
    if (fromEnv && fromEnv.length > 0) {
        return fromEnv;
    }
    try {
        const path = new URL(fallbackUrl, window.location.origin).pathname;
        const base = path.split('/').pop();
        if (base && base.includes('.')) {
            return base;
        }
    }
    catch (e) {
        console.error('[extensionPackageDownload] failed to parse URL for filename', e);
    }
    return DEFAULT_EXTENSION_PACKAGE_FILENAME;
}
/** Parses RFC 5987 / legacy filename from Content-Disposition (download responses). */
export function parseContentDispositionFilename(header) {
    if (!header || typeof header !== 'string') {
        return undefined;
    }
    const star = /filename\*=(?:UTF-8''|)([^;\s]+)/i.exec(header);
    if (star?.[1]) {
        const raw = star[1].trim().replace(/^["']|["'];?$/g, '');
        try {
            return decodeURIComponent(raw);
        }
        catch {
            return raw;
        }
    }
    const quoted = /filename="([^"]+)"/i.exec(header);
    if (quoted?.[1]) {
        return quoted[1];
    }
    const plain = /filename=([^;\s]+)/i.exec(header);
    if (plain?.[1]) {
        return plain[1].replace(/^["']|["']$/g, '');
    }
    return undefined;
}
function isSafeExtensionArtifactBasename(name) {
    if (name.includes('/') || name.includes('\\') || name.includes('..')) {
        return false;
    }
    return /^cricket-studio-extension-.+\.zip$/i.test(name);
}
/**
 * Prefer the API’s Content-Disposition / X-Extension-Version headers so split-hosted portals
 * (fetch + blob download) save as `cricket-studio-extension-<version>.zip` instead of a generic basename.
 */
export function resolveExtensionPackageFileNameFromResponse(res, fallbackHref, latestArtifact) {
    const fromCd = parseContentDispositionFilename(res.headers.get('content-disposition'));
    if (fromCd && isSafeExtensionArtifactBasename(fromCd)) {
        return fromCd;
    }
    const ver = res.headers.get('x-extension-version')?.trim();
    if (ver && /^[0-9A-Za-z.+\-]{1,64}$/.test(ver)) {
        const candidate = `cricket-studio-extension-${ver}.zip`;
        if (isSafeExtensionArtifactBasename(candidate)) {
            return candidate;
        }
    }
    if (latestArtifact?.filename && isSafeExtensionArtifactBasename(latestArtifact.filename)) {
        return latestArtifact.filename;
    }
    return getExtensionPackageFileName(fallbackHref);
}
async function fetchLatestExtensionArtifactMetadata(browserTarget) {
    const metadataUrl = apiUrl(getExtensionArtifactMetadataApiPath(browserTarget));
    try {
        const res = await fetch(metadataUrl, {
            mode: 'cors',
            credentials: 'include',
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
            console.warn('[extensionPackageDownload] latest artifact metadata request failed', {
                browserTarget,
                status: res.status,
            });
            return undefined;
        }
        const payload = await res.json();
        if (isExtensionArtifactDownloadMetadata(payload)) {
            return payload;
        }
        console.warn('[extensionPackageDownload] latest artifact metadata response was invalid', {
            browserTarget,
        });
    }
    catch (e) {
        console.warn('[extensionPackageDownload] latest artifact metadata request threw', {
            browserTarget,
            error: e,
        });
    }
    return undefined;
}
function addArtifactCacheKey(resolvedDownloadUrl, latestArtifact) {
    const next = new URL(resolvedDownloadUrl.toString());
    if (!latestArtifact) {
        return next;
    }
    next.searchParams.set('artifactId', latestArtifact.id);
    next.searchParams.set('artifactSha256', latestArtifact.sha256);
    return next;
}
function openInstallHelpModal() {
    window.dispatchEvent(new CustomEvent(EXTENSION_BANNER_OPEN_INSTALL_MODAL_EVENT));
}
function triggerBrowserDownload(href, filename) {
    const a = document.createElement('a');
    a.setAttribute('href', href);
    a.setAttribute('download', filename);
    a.setAttribute('rel', 'noopener');
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}
/**
 * If a downloadable package URL is configured, saves the file to the user’s default download location
 * and opens the in-app install guide. Otherwise opens the install guide only.
 */
export function downloadExtensionPackageOrOpenInstallHelp() {
    const browserTarget = getDetectedBrowserTarget();
    const backendArtifactPath = getExtensionArtifactDownloadApiPath(browserTarget);
    const configuredHref = getConfiguredExtensionPackageDownloadUrl();
    const href = configuredHref || apiUrl(backendArtifactPath);
    const usesBackendArtifactRoute = !configuredHref;
    if (!href) {
        console.info('[extensionPackageDownload] no package file URL; opening install help modal only', {
            reason: 'missing_href',
        });
        openInstallHelpModal();
        return;
    }
    const provisionalFilename = getExtensionPackageFileName(href);
    let resolved;
    try {
        resolved = new URL(href, window.location.href);
    }
    catch (e) {
        console.error('[extensionPackageDownload] invalid href; opening install help modal', {
            reason: 'invalid_url',
            error: e,
        });
        openInstallHelpModal();
        return;
    }
    console.info('[extensionPackageDownload] resolved package URL; opening install help modal', {
        origin: resolved.origin,
        sameOrigin: resolved.origin === window.location.origin,
        filename: provisionalFilename,
        usesBackendArtifactRoute,
        browserTarget,
    });
    openInstallHelpModal();
    if (resolved.origin === window.location.origin && !usesBackendArtifactRoute) {
        triggerBrowserDownload(resolved.href, provisionalFilename);
        console.info('[extensionPackageDownload] same-origin download started', { filename: provisionalFilename });
        return;
    }
    void (async () => {
        let latestArtifact;
        let downloadUrl = new URL(resolved.toString());
        try {
            if (usesBackendArtifactRoute) {
                latestArtifact = await fetchLatestExtensionArtifactMetadata(browserTarget);
                downloadUrl = addArtifactCacheKey(downloadUrl, latestArtifact);
                if (latestArtifact) {
                    console.info('[extensionPackageDownload] tied backend artifact download to latest metadata', {
                        artifactId: latestArtifact.id,
                        version: latestArtifact.version,
                        browserTarget: latestArtifact.browserTarget,
                        sha256Prefix: latestArtifact.sha256.slice(0, 12),
                    });
                }
            }
            const res = await fetch(downloadUrl.toString(), {
                mode: 'cors',
                credentials: usesBackendArtifactRoute ? 'include' : 'omit',
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const filename = usesBackendArtifactRoute
                ? resolveExtensionPackageFileNameFromResponse(res, downloadUrl.toString(), latestArtifact)
                : provisionalFilename;
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            try {
                triggerBrowserDownload(objectUrl, filename);
                console.info('[extensionPackageDownload] cross-origin download started', { filename });
            }
            finally {
                setTimeout(() => {
                    URL.revokeObjectURL(objectUrl);
                }, 60000);
            }
        }
        catch (e) {
            console.error('[extensionPackageDownload] CORS or fetch failed during package download', {
                urlOrigin: resolved.origin,
                backendArtifactRoute: usesBackendArtifactRoute,
                error: e,
            });
            if (!usesBackendArtifactRoute) {
                window.open(resolved.toString(), '_blank', 'noopener,noreferrer');
            }
        }
    })();
}
